import styled, { css } from 'styled-components';
import { userSelectNone } from '../../../../../styles/mixins';

const narrowViewportStyles = css`
  @container (max-width: 300px) {
    width: 40px !important;
  }

  span {
    flex-shrink: 0;
    @container (max-width: 300px) {
      display: none;
    }
  }
`;

const overlayPanel = css`
  ${userSelectNone};
  box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};
  border-radius: 8px;
  background-color: #fff;
  position: absolute;
  top: 16px;
  cursor: pointer;
  z-index: 1;
`;

const ControlElementBasicStyles = css`
  ${overlayPanel};
  transition: border-color 0.2s ease-in-out;
  height: 40px;
  width: 40px;
  background-color: white;
  border: 2px solid #fff;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  display: flex;

  ${narrowViewportStyles};
`;

export const MapControlsContainer = styled.div`
  container-type: inline-size;
  z-index: 3;

  button {
    &:hover {
      background-color: white;
      box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
    }
  }
`;

export const ExpandMap = styled.button<{ expand: boolean; evolutionEnabled: boolean }>`
  ${ControlElementBasicStyles};
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 6;
  padding: 0 12px;
  width: auto;
  border: 0;

  ${({ evolutionEnabled }) =>
    evolutionEnabled &&
    css`
      left: unset;
      right: 24px;
      padding: 0 10px;

      img {
        width: 20px;
        height: 20px;
      }
    `}

  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
  }

  span {
    margin-left: 10px;
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  }

  img {
    ${userSelectNone};
    ${({ expand }) =>
      expand &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

// MPH-981 (Figma 979-41667): the Evo detail dialog stacks the satellite toggle and the airports over it on
// the right edge, 24px in on desktop — mobile keeps the Evo 16px stack.
const evoDialogDesktopStack = (bottom: string) => css`
  @media (min-width: 1024px) {
    left: unset;
    right: 24px;
    bottom: ${bottom};
  }
`;

export const MapStyle = styled.button<{ $image: string; evolutionEnabled?: boolean; $isDialog?: boolean }>`
  ${ControlElementBasicStyles};
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 6;

  @media (min-width: 1024px) {
    top: unset;
    right: unset;
    bottom: 24px;
    left: 24px;
  }

  ${({ $image }) =>
    $image &&
    css`
      background-image: url(${$image});
    `}

  ${({ evolutionEnabled }) =>
    evolutionEnabled &&
    css`
      top: unset;
      left: unset;
      bottom: 16px;
      right: 16px;
    `}

  ${({ evolutionEnabled, $isDialog }) => evolutionEnabled && $isDialog && evoDialogDesktopStack('24px')}

  &:hover {
    border-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
  }
`;

export const ShowAirportsStyle = styled.button<{ evolutionEnabled?: boolean; $isDialog?: boolean }>`
  ${ControlElementBasicStyles};
  position: absolute;
  top: 64px;
  right: 16px;
  z-index: 6;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  /* inherit typography from container (Figma Button tokens) */
  border: 0;

  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
  }

  @media (min-width: 1024px) {
    top: unset;
    right: unset;
    bottom: 24px;
    left: 72px;
    padding: 10px 16px;
    width: auto;
    /* A gap, not an icon margin: the icon's own margin holds its footprint. */
    gap: 8px;
  }

  ${({ evolutionEnabled }) =>
    evolutionEnabled &&
    css`
      padding: 0 10px;
      top: unset;
      bottom: 64px;

      img {
        margin: 0;
        width: 20px;
        height: 20px;
      }

      @media (min-width: 1024px) {
        top: unset;
        right: unset;
        bottom: 24px;
        left: 72px;
        padding: 0 10px;
      }
    `}

  ${({ evolutionEnabled, $isDialog }) => evolutionEnabled && $isDialog && evoDialogDesktopStack('72px')}
`;

export const ResetMap = styled.button`
  ${ControlElementBasicStyles};
  flex-direction: row;
  top: 16px;
  padding: 0 16px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  cursor: pointer;
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  width: auto;

  @media (min-width: 1024px) {
    top: 24px;
  }

  /* A gap, not an icon margin: the icon's own margin holds its footprint. */
  gap: 8px;
  z-index: 8;
`;

export const CloseMap = styled.button<{ evolutionEnabled?: boolean }>`
  ${ControlElementBasicStyles};
  position: absolute;
  top: 16px;
  left: 16px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

  ${({ evolutionEnabled }) =>
    evolutionEnabled &&
    css`
      left: unset;
      right: 16px;
    `}
`;

export const HideMap = styled.button`
  ${ControlElementBasicStyles};
  position: absolute;
  padding: 8px 16px;
  top: 24px;
  left: 16px;
  width: auto;
  font-size: ${({ theme }) => theme.kallisto.font.size['200']};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

  @media (max-width: 1200px) {
    display: none;
  }
`;
